import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotedStepDefect, StepCheckList } from '@testflow/shared';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { ApiError } from '../../lib/api.js';
import type { Step } from '../../lib/catalog-api.js';
import { pickLang } from '../../lib/pick-lang.js';
import { runsApi, stepChecksApi } from '../../lib/runs-api.js';
import { toast } from '../../lib/toast.js';
import { DefectCreateDialog, type DefectStepContext } from '../defects/defect-create-dialog.js';

// #208: Die Schritt-Tabelle der Durchführung. Anlass: ein BW-Testfall mit 160 Schritten, je einer
// Datenquelle — der Name steht im Schritt-Input, den die alte Tabelle gar nicht zeigte, und ein Lauf
// hat nur EIN Ergebnis. Deshalb hier: Input-Spalte, ein Haken je Schritt (Merkhilfe des Testers,
// kein Ergebnis), ein Knopf „Fehler notieren“ je Schritt (nicht blockierend, ohne Lauf) und — sobald alle
// Schritte abgehakt sind — die Frage, ob der Testfall damit bestanden ist.
//
// Bewusst NICHT hier: ein Ergebnis buchen. „Ja" ruft nur `onConfirmPass` des Panels (= derselbe Weg
// wie der Bestanden-Knopf) und ist mit DEMSELBEN Grund gesperrt — sonst liefe „Ja" in einer Kette
// mit Nachfolger und leerem Übergabetext in einen stillen 400 (Review Focus 5).
//
// Die Haken-Spalte gibt es erst ab zwei Schritten: bei genau einem ist „abgehakt" dasselbe wie
// „bestanden", die Aktionsleiste reicht. Input und Defect-Knopf bleiben auch dann.

export function StepChecklist({
  projectId,
  planId,
  testCaseId,
  testCaseHumanId,
  steps,
  lang,
  canWrite,
  passBlockedReason,
  onConfirmPass,
}: {
  projectId: string;
  planId: string;
  testCaseId: string;
  testCaseHumanId: string;
  // Bereits nach step_no sortiert (orderedSteps des Panels).
  steps: Step[];
  lang: string;
  // #208: false = Ketten-Sperre des Panels (`runBlocked`). Der Planstatus kommt dagegen vom Server
  // über `can_write` — das Panel kennt ihn nicht.
  canWrite: boolean;
  passBlockedReason: string | null;
  onConfirmPass: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(false);
  const [defectStep, setDefectStep] = useState<Step | null>(null);
  // Stabil halten: der Effekt im Dialog haengt an onClose (Escape-Listener, Fokus).
  const closeDefect = useCallback(() => setDefectStep(null), []);

  const multi = steps.length > 1;
  const queryKey = ['step-checks', projectId, planId, testCaseId];
  // Auch bei genau einem Schritt geladen: `can_write` entscheidet über den Defect-Knopf.
  const checksQuery = useQuery({
    queryKey,
    queryFn: () => stepChecksApi.list(projectId, planId, testCaseId),
    enabled: steps.length > 0,
    // #208: 403 = Rolle ohne `run.execute` (z. B. Lesende) — das ist eine Antwort, kein Ausfall.
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 1,
  });
  const forbidden = checksQuery.error instanceof ApiError && checksQuery.error.status === 403;
  // #208: ein anderer Ladefehler als 403 ist ein Ausfall — er wird gezeigt (mit Neu laden), nie
  // still als „keine Haken" (Muster my-defect-details.tsx, #89).
  const loadFailed = checksQuery.isError && !forbidden;
  const writable = canWrite && checksQuery.data?.can_write === true;
  // #208: Der Defect-Knopf hängt nicht an den Haken. Ohne `run.execute` (403) gibt es ihn nicht;
  // solange die Haken laden oder scheitern, entscheidet die Ketten-Sperre des Panels — den
  // geschlossenen Plan weist der Server ab (409). Erst eine Antwort mit `can_write` sperrt ihn.
  const showDefect = !forbidden;
  const defectEnabled = canWrite && (checksQuery.data ? checksQuery.data.can_write : true);
  // #208 Nachtrag: eigene notierte Fehler dieser Durchführung — Markierung je Defect am Schritt.
  // 403 (ohne run.execute) und Ladefehler zeigen schlicht keine Markierung, wie bei den Haken.
  const notedQuery = useQuery({
    queryKey: ['noted-step-defects', projectId, planId, testCaseId],
    queryFn: () => runsApi.listNotedStepDefects(projectId, planId, testCaseId),
    enabled: steps.length > 0,
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 1,
  });
  // Zuordnung über die Schrittnummer: der Server liefert den Schritt als failed_step (step_no);
  // Defects ohne Schritt (failed_step null) haben keine Zeile und bekommen keine Markierung.
  const notedByStepNo = new Map<number, NotedStepDefect[]>();
  for (const d of notedQuery.data?.defects ?? []) {
    if (!d.failed_step) continue;
    const list = notedByStepNo.get(d.failed_step.step_no) ?? [];
    list.push(d);
    notedByStepNo.set(d.failed_step.step_no, list);
  }
  const checkedIds = new Set(checksQuery.data?.step_ids ?? []);
  // #208 (Review Focus 3): gezählt wird gegen die AKTUELL geladenen Schritte — ein Haken an einem
  // inzwischen gelöschten Schritt zählt nicht mit.
  const done = steps.filter((s) => checkedIds.has(s.step_id)).length;
  const showChecks = multi && !forbidden;
  const allDone = showChecks && Boolean(checksQuery.data) && done === steps.length;

  const toggle = useMutation({
    mutationFn: ({ stepId, checked }: { stepId: string; checked: boolean }) =>
      checked
        ? stepChecksApi.check(projectId, planId, testCaseId, stepId)
        : stepChecksApi.uncheck(projectId, planId, testCaseId, stepId),
    // Optimistisch umschalten, bei Fehler zurückrollen (den Fehler-Toast setzt der zentrale
    // MutationCache, lib/query-client.ts).
    onMutate: async ({ stepId, checked }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<StepCheckList>(queryKey);
      if (previous) {
        const ids = previous.step_ids.filter((id) => id !== stepId);
        queryClient.setQueryData<StepCheckList>(queryKey, {
          ...previous,
          step_ids: checked ? [...ids, stepId] : ids,
        });
      }
      return { wasChecked: previous ? previous.step_ids.includes(stepId) : !checked };
    },
    // #208 Final-Review: nur DIESEN Schritt auf den aktuellen Cache zurückdrehen. Ein ganzer
    // Schnappschuss von vorher würde einen zweiten, inzwischen erfolgreichen Haken mit wegwischen.
    onError: (_err, { stepId }, ctx) => {
      if (!ctx) return;
      queryClient.setQueryData<StepCheckList>(queryKey, (current) => {
        if (!current) return current;
        const ids = current.step_ids.filter((id) => id !== stepId);
        return { ...current, step_ids: ctx.wasChecked ? [...ids, stepId] : ids };
      });
    },
    // Danach gilt wieder der Stand des Servers.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const onToggle = (stepId: string, checked: boolean) => {
    // „Nein" gilt nur, bis sich ein Haken ändert.
    setDismissed(false);
    toggle.mutate({ stepId, checked });
  };

  const defectContext: DefectStepContext | null = defectStep
    ? {
        planId,
        testCaseId,
        testCaseHumanId,
        stepId: defectStep.step_id,
        stepNo: defectStep.step_no,
        tcode: defectStep.tcode,
        inputText: pickLang(lang, defectStep.input_de, defectStep.input_en) || null,
      }
    : null;

  return (
    <div className="space-y-2">
      {loadFailed && <QueryErrorNotice compact onRetry={() => void checksQuery.refetch()} />}
      {showChecks && checksQuery.data && (
        <p className="text-xs text-text-muted">
          {t('exec.stepList.progress', { done, total: steps.length })}
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] text-[11px] font-bold uppercase tracking-wide text-text-muted">
              {showChecks && (
                <th className="w-8 px-1.5 py-1 text-left">{t('exec.stepList.doneColumn')}</th>
              )}
              <th className="w-10 px-1.5 py-1 text-left">{t('catalog.fields.stepNo')}</th>
              <th className="px-1.5 py-1 text-left">{t('catalog.fields.description')}</th>
              <th className="w-24 px-1.5 py-1 text-left">{t('catalog.fields.tcode')}</th>
              <th className="px-1.5 py-1 text-left">{t('catalog.fields.input')}</th>
              <th className="px-1.5 py-1 text-left">{t('catalog.fields.expectedOutput')}</th>
              {showDefect && (
                <th className="w-20 px-1.5 py-1 text-left">{t('exec.stepList.actionColumn')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {steps.map((step) => (
              <tr key={step.step_id} className="border-b border-[var(--border)] last:border-0">
                {showChecks && (
                  <td className="px-1.5 py-1">
                    <input
                      type="checkbox"
                      checked={checkedIds.has(step.step_id)}
                      disabled={!writable}
                      onChange={(e) => onToggle(step.step_id, e.target.checked)}
                      aria-label={t('exec.stepList.checkLabel', { no: step.step_no })}
                    />
                  </td>
                )}
                <td className="px-1.5 py-1 text-text-muted">{step.step_no}</td>
                <td className="px-1.5 py-1 text-text-body">
                  {pickLang(lang, step.description_de, step.description_en)}
                  {(notedByStepNo.get(step.step_no) ?? []).map((d) => (
                    <span
                      key={d.defect_id}
                      title={d.title}
                      className="ml-2 inline-block rounded-btn border border-status-fail px-1.5 py-0.5 text-[11px] font-semibold text-status-fail"
                    >
                      {`⚠ ${d.human_id}`}
                    </span>
                  ))}
                </td>
                <td className="px-1.5 py-1 text-text-body">{step.tcode ?? '—'}</td>
                <td className="px-1.5 py-1 text-text-body">
                  {pickLang(lang, step.input_de, step.input_en) || '—'}
                </td>
                <td className="px-1.5 py-1 text-text-body">
                  {pickLang(lang, step.expected_output_de, step.expected_output_en) || '—'}
                </td>
                {showDefect && (
                  <td className="px-1.5 py-1">
                    <button
                      type="button"
                      onClick={() => setDefectStep(step)}
                      disabled={!defectEnabled}
                      title={t('exec.stepList.defectTooltip')}
                      aria-label={t('exec.stepList.defectLabel', { no: step.step_no })}
                      className="rounded-btn border border-[var(--border)] px-2 py-0.5 text-xs text-text-body hover:bg-[var(--bg-subtle)] disabled:opacity-60"
                    >
                      {t('exec.stepList.defect')}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {allDone && !dismissed && (
        <div className="flex flex-wrap items-center gap-3 rounded-card border border-status-pass bg-status-pass/10 px-3 py-2 text-sm">
          <span className="text-text-body">{t('exec.stepList.allDoneQuestion')}</span>
          <button
            type="button"
            onClick={onConfirmPass}
            disabled={Boolean(passBlockedReason)}
            aria-describedby={passBlockedReason ? 'exec-steps-pass-blocked' : undefined}
            className="rounded-btn bg-status-pass px-3 py-1 text-sm font-bold text-white disabled:opacity-60"
          >
            {t('exec.stepList.yes')}
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="rounded-btn border border-[var(--border)] px-3 py-1 text-sm text-text-body hover:bg-[var(--bg-subtle)]"
          >
            {t('exec.stepList.no')}
          </button>
          {passBlockedReason && (
            <span id="exec-steps-pass-blocked" className="text-xs text-status-fail">
              {passBlockedReason}
            </span>
          )}
        </div>
      )}

      {defectContext && (
        <DefectCreateDialog
          projectId={projectId}
          stepContext={defectContext}
          onClose={closeDefect}
          // Der Tester bleibt in der Durchführung — den neuen Defect findet er unter „Meine Defects".
          // #208 Final-Review: eine Bestätigung mit der Defect-ID, sonst schlösse der Dialog
          // kommentarlos. Nur hier im Schritt-Modus; das Board öffnet stattdessen das Panel.
          onCreated={(defect) =>
            toast.success(t('exec.stepList.defectCreated', { humanId: defect.human_id }))
          }
        />
      )}
    </div>
  );
}
